import React from 'react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { Copy, LogOut, Droplet, Check, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { LOGO_URL, truncateAddress } from '../lib/format';
import { useVault } from '../context/VaultContext';
import { usePalmentWallet } from '../context/DemoWalletContext';

export default function Header() {
  const { connected, address, mode, startDemo, disconnectAll } = usePalmentWallet();
  const { claimFaucet, hasFaucetClaimed } = useVault();
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    if (!address) return;
    await navigator.clipboard.writeText(address);
    setCopied(true);
    toast.success('Address copied', {
      description: truncateAddress(address, 6, 6),
    });
    setTimeout(() => setCopied(false), 1500);
  };

  const [faucetBusy, setFaucetBusy] = React.useState(false);
  const handleFaucet = async () => {
    if (faucetBusy) return;
    setFaucetBusy(true);
    try {
      const res = await claimFaucet();
      toast.success('1,000 Mock PUSD minted to your wallet', {
        description: res?.signature
          ? `Devnet memo tx: ${res.signature.slice(0, 8)}…${res.signature.slice(-6)}`
          : 'Devnet testnet tokens are for simulation only.',
      });
    } catch (err) {
      console.error('[palment] faucet failed', err);
      const msg = (err && (err.message || err.toString())) || 'Unknown error';
      const rejected = /reject|denied|user|declin/i.test(msg);
      toast.error(rejected ? 'Faucet rejected in wallet' : 'Faucet failed', {
        description: rejected ? undefined : msg.slice(0, 160),
      });
    } finally {
      setFaucetBusy(false);
    }
  };

  const handleDemo = () => {
    startDemo();
    toast.success('Demo mode enabled', {
      description: 'Explore the full vault flow — no real wallet required.',
      icon: <Sparkles size={16} />,
    });
  };

  return (
    <header
      className="sticky top-0 z-40 border-b border-white/5"
      data-testid="app-header"
    >
      <div className="absolute inset-0 -z-10 bg-[#041411]/70 backdrop-blur-xl" />
      <div className="max-w-7xl mx-auto px-6 sm:px-8 h-[72px] flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3 group" data-testid="brand-link">
          <div
            className="w-14 h-14 rounded-xl p-[1.5px]"
            style={{
              background:
                'linear-gradient(135deg, #E6E8EC 0%, #A8ADB7 50%, #6B7280 100%)',
            }}
          >
            <div
              className="w-full h-full rounded-[10px] flex items-center justify-center overflow-hidden"
              style={{
                background:
                  'linear-gradient(135deg, #F4F5F7 0%, #D0D4DB 60%, #9AA0AB 100%)',
              }}
            >
              <img src={LOGO_URL} alt="PALMENT" className="w-[45px] h-[45px]" />
            </div>
          </div>
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-display font-black tracking-[0.18em] text-[13px] text-white/95">
              PALMENT
            </span>
            <span className="text-[10px] tracking-[0.2em] text-palm-mint/60 uppercase mt-1">
              yield vault · solana
            </span>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-8 text-sm text-white/70">
          <a href="#vault" className="hover:text-palm-mint transition-colors" data-testid="nav-vault">
            Vault
          </a>
          <a href="#stats" className="hover:text-palm-mint transition-colors" data-testid="nav-stats">
            Analytics
          </a>
          <a href="#how" className="hover:text-palm-mint transition-colors" data-testid="nav-how">
            How it works
          </a>
          <a href="#security" className="hover:text-palm-mint transition-colors" data-testid="nav-security">
            Security
          </a>
        </nav>

        <div className="flex items-center gap-3">
          {connected ? (
            <>
              <button
                type="button"
                onClick={handleFaucet}
                disabled={faucetBusy}
                className="hidden sm:inline-flex items-center gap-2 h-10 px-4 rounded-xl glass hover:border-palm-lime/40 text-sm font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="faucet-button"
              >
                <Droplet size={15} strokeWidth={1.8} className="text-palm-lime" />
                <span className="text-white/90">
                  {faucetBusy
                    ? 'Signing…'
                    : hasFaucetClaimed
                      ? 'Faucet · +1K PUSD'
                      : 'Claim 1,000 PUSD'}
                </span>
              </button>

              <div className="flex items-center gap-2 h-10 px-3 rounded-xl glass" data-testid="wallet-pill">
                <span
                  className={`w-2 h-2 rounded-full ${
                    mode === 'real'
                      ? 'bg-palm-lime shadow-[0_0_10px_rgba(227,239,38,0.9)]'
                      : 'bg-palm-mint/80'
                  } animate-pulseSlow`}
                  aria-hidden
                />
                <span className="font-mono text-xs text-white/90" data-testid="wallet-address">
                  {truncateAddress(address, 5, 4)}
                </span>
                {mode === 'demo' && (
                  <span className="hidden md:inline text-[9px] tracking-[0.22em] uppercase text-palm-mint/70 border border-palm-mint/30 rounded-full px-1.5 py-0.5">
                    demo
                  </span>
                )}
                <button
                  type="button"
                  onClick={handleCopy}
                  className="ml-1 text-white/50 hover:text-palm-lime transition-colors"
                  aria-label="Copy address"
                  data-testid="copy-address-button"
                >
                  {copied ? <Check size={14} strokeWidth={2} /> : <Copy size={14} strokeWidth={1.8} />}
                </button>
                <button
                  type="button"
                  onClick={disconnectAll}
                  className="ml-1 text-white/50 hover:text-red-300 transition-colors"
                  aria-label="Disconnect"
                  data-testid="disconnect-button"
                >
                  <LogOut size={14} strokeWidth={1.8} />
                </button>
              </div>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={handleDemo}
                className="hidden sm:inline-flex items-center gap-2 h-10 px-4 rounded-xl glass hover:border-palm-mint/40 text-sm font-semibold text-white/80 transition-all"
                data-testid="demo-mode-button"
              >
                <Sparkles size={14} strokeWidth={1.8} className="text-palm-mint" />
                Try demo mode
              </button>
              <WalletMultiButton data-testid="connect-wallet-button" />
            </>
          )}
        </div>
      </div>
    </header>
  );
}
